import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PostAuthentication, PostAuthenticationClientCredentials } from 'services/authentication/postAuthentication';
import { PostObtainUserLogin } from 'services/authentication/postObtainUserLogin';
import { PostValidateDocument } from 'services/authentication/postValidateDocument';

import { PostSendToken } from 'services/authentication/postSendToken';
import { PostUpdatePassword } from 'services/authentication/postUpdatePassword';
import { PostValidateEmailLogin } from 'services/authentication/postValidateEmailLogin';
import { PostValidatePasswordToken } from 'services/authentication/postValidatePasswordToken';

import { PostGetContacts } from 'services/authentication/postGetContacts';
import { PostCreateLoginApp } from 'services/authentication/postCreateLoginApp';
import { GetActiveLoginBanner } from 'services/authentication/getActiveLoginBanner';

import { ParamsBodyAuthentication, ParamsBodyValidateDocument, ParamsBodyObtainUserLogin, ParamsDataUserFound, ParamsDataUserAccountPassword, ParamsBodyPostSendToken, ParamsBodyPostValidatePasswordToken, ParamsBodyPostUpdatePassword, ParamsBodyPostCreateLoginApp, ParamsDataAuthenticationExternal } from 'utils/typeServices';

const useAuthentication = () => {
    const navigate = useNavigate();
    const [dataSendToken, setDataSendToken] = useState<boolean>();
    const [isShowModalAlert, setIsShowModalAlert] = useState(true);
    const [isLoadingBanner, setIsLoadingBanner] = useState(true);
    const [activeLoginBanner, setActiveLoginBanner] = useState<string | null>(null);
    const [dataCreateLoginApp, setDataCreateLoginApp] = useState<boolean | string>();
    const [dataUpdatePassword, setDataUpdatePassword] = useState<boolean>();
    const [dataValidatePasswordToken, setDataValidatePasswordToken] = useState<boolean>();
    const [dataValidateDocument, setDataValidateDocument] = useState<ParamsDataUserFound>();
    const [dataTokenExternal, setDataTokenExternal] = useState<ParamsDataAuthenticationExternal | false>();
    const [dataUserAccountPassword, setDataUserAccountPassword] = useState<ParamsDataUserAccountPassword | false>();
    const [errorLogin, setErrorLogin] = useState({
        isError: false,
        login: '',
        password: '',
    });

    const handleClearAuthentication = () => {
        setDataSendToken(undefined);
        setDataCreateLoginApp(undefined);
        setDataUpdatePassword(undefined);
        setDataValidatePasswordToken(undefined);
        setDataValidateDocument(undefined);
        setDataUserAccountPassword(undefined);
        setErrorLogin({
            isError: false,
            login: '',
            password: '',
        });
    };

    const handleAlertModal = (isShow: boolean) => {
        setIsShowModalAlert(isShow);
    }

    const handleGetActiveLoginBanner = async () => {
        const { data } = await GetActiveLoginBanner();
        setActiveLoginBanner(data);
        setIsLoadingBanner(false);
    }

    const handlePostAuthentication = async (bodyAuthentication: ParamsBodyAuthentication) => {
        setErrorLogin({ isError: false, login: '', password: '' });
        const { data } = await PostAuthentication(bodyAuthentication);

        if (data) {
            let dateNow = new Date();
            localStorage.clear();
            localStorage.setItem('token', data.access_token);
            localStorage.setItem('expires', data.expires_in);
            localStorage.setItem('time_expires', `${dateNow.getTime()}`);
            navigate('home');
        } else {
            setErrorLogin({ isError: true, login: bodyAuthentication.usuario, password: bodyAuthentication.password });
        }
    }

    const handlePostAuthenticationClientCredentials = async () => {
        const { data } = await PostAuthenticationClientCredentials();

        if (data) {
            let dateNow = new Date();
            localStorage.clear();
            localStorage.setItem('token_external', data.access_token);
            localStorage.setItem('expires_external', `${dateNow.getTime() + (data.expires_in * 1000)}`);

            setDataTokenExternal({
                tokenExternal: data.access_token,
                expiresExternal: `${data.expires_in * 1000}`,
                timeExpiresExternal: `${dateNow.getTime()}`
            });
        } else {
            setDataTokenExternal(false);
        }
    }

    const handlePostValidateDocument = async (body: ParamsBodyValidateDocument) => {
        const { data } = await PostValidateDocument(body);

        if (data && data?.UsuarioEncontrado) {
            setDataValidateDocument({ userFound: true, Frotista: data.Frotista, UsuarioCadastrado: data.UsuarioCadastrado, UsuarioEncontrado: data.UsuarioEncontrado });
        } else {
            setDataValidateDocument({ userFound: false });
        }
    }

    const handlePostObtainUserLogin = async (body: ParamsBodyObtainUserLogin, sendObtainUser: boolean) => {
        const { data } = await PostObtainUserLogin({ body, sendObtainUser });

        if (typeof data === 'string') {
            setDataValidateDocument({ userFound: true, obtainUserLogin: true, UsuarioEncontrado: true, UsuarioCadastrado: true, userLogin: data });
        } else if (data) {
            setDataValidateDocument({ userFound: true, obtainUserLogin: true, UsuarioEncontrado: true, UsuarioCadastrado: true, userLogin: '' });
        } else {
            setDataValidateDocument({ userFound: true, obtainUserLogin: false, userLogin: '' });
        }
    }

    const handlePostGetContacts = async (body: string) => {
        setDataUserAccountPassword(undefined);
        const { data } = await PostGetContacts({ body });

        if (data) {
            setDataUserAccountPassword(data);
        } else {
            setDataUserAccountPassword(false);
        }
    }

    const handlePostValidateEmailLogin = async (body: string) => {
        setDataUserAccountPassword(undefined);
        const { data } = await PostValidateEmailLogin({ body });

        if (data !== null && typeof data === 'object') {
            setDataUserAccountPassword(data);
        } else {
            setDataUserAccountPassword(false);
        }
    }

    const handlePostSendToken = async (body: ParamsBodyPostSendToken) => {
        setDataSendToken(undefined);
        const { data } = await PostSendToken({ body });

        if (data) {
            setDataSendToken(true);
        } else {
            setDataSendToken(false);
        }
    }

    const handlePostValidatePasswordToken = async (body: ParamsBodyPostValidatePasswordToken) => {
        const { data } = await PostValidatePasswordToken({ body });

        if (data) {
            setDataValidatePasswordToken(true);
        } else {
            setDataValidatePasswordToken(false);
        }
    }

    const handlePostUpdatePassword = async (body: ParamsBodyPostUpdatePassword) => {
        const { data } = await PostUpdatePassword({ body });

        if (data) {
            setDataUpdatePassword(true);
        } else {
            setDataUpdatePassword(false);
        }
    }

    const handlePostCreateLoginApp = async (body: ParamsBodyPostCreateLoginApp) => {
        setDataCreateLoginApp(undefined);
        const { CadastradoComSucesso, errorMsg } = await PostCreateLoginApp(body);

        if (CadastradoComSucesso) {
            setDataCreateLoginApp(true);
        } else if (errorMsg) {
            setDataCreateLoginApp(errorMsg);
        } else {
            setDataCreateLoginApp(false);
        }
    }

    return { errorLogin, dataValidateDocument, dataUserAccountPassword, dataSendToken, dataValidatePasswordToken, dataUpdatePassword, dataCreateLoginApp, isShowModalAlert, isLoadingBanner, activeLoginBanner, dataTokenExternal, handlePostAuthentication, handlePostValidateDocument, handlePostObtainUserLogin, handlePostValidateEmailLogin, handlePostSendToken, handlePostValidatePasswordToken, handlePostUpdatePassword, handlePostGetContacts, handlePostCreateLoginApp, handleClearAuthentication, handleAlertModal, handleGetActiveLoginBanner, handlePostAuthenticationClientCredentials };
}

export default useAuthentication;